import React, { useState, useEffect } from 'react';
import {
  isTokenValid, startGoogleOAuth, clearTokens,
  getOAuthError, clearOAuthError,
  listCalendars, listEventsMulti, createEvent, updateEvent, deleteEvent,
  type GoogleEvent, type CalendarListEntry, type EventInput,
} from '../../services/googleCalendar';
import { matchEintrag } from './services/smartMatcher';
import { loadSetting, saveSetting } from '../../services/settingsService';
import { EndOfDayReview } from './components/EndOfDayReview';
import ErinnerungButton from '../../components/ErinnerungButton';
import { useHandoffStore } from '../../store/handoffStore';
import { eventIdsMitProtokoll } from '../../services/protokollService';

// ── Typen ─────────────────────────────────────────────────────────────────

interface GCalConfig {
  clientId:     string;
  calendarId:   string;        // Rückwärtskompat. / Standardkalender (Einzelwert)
  calendarIds?: string[];      // ausgewählte Kalender (Mehrfachauswahl)
  clientSecret?: string;
}

const CONFIG_KEY = 'gcal_config';

// ── Setup-Screen ──────────────────────────────────────────────────────────

function GCalSetup({ onConnected }: { onConnected: () => void }): React.JSX.Element {
  const [clientId,     setClientId]     = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [calendarId,   setCalendarId]   = useState('primary');
  const [saving,       setSaving]       = useState(false);
  const [oauthError]   = useState<string | null>(() => getOAuthError());

  // Bereits gespeicherte Client-ID/Kalender-ID vorausfüllen (Secret wurde nie
  // gespeichert und muss neu eingegeben werden).
  useEffect(() => {
    void loadSetting<GCalConfig | null>(CONFIG_KEY, null).then(cfg => {
      if (cfg !== null) {
        setClientId(cfg.clientId);
        setCalendarId(cfg.calendarId);
      }
    });
  }, []);

  const handleConnect = async (): Promise<void> => {
    if (!clientId.trim() || !clientSecret.trim()) return;
    setSaving(true);
    clearOAuthError();
    await saveSetting<GCalConfig>(CONFIG_KEY, {
      clientId:     clientId.trim(),
      calendarId,
      clientSecret: clientSecret.trim(),
    });
    await startGoogleOAuth(clientId.trim());
    // Page navigates away for OAuth
  };

  return (
    <div className="max-w-lg mx-auto space-y-5 py-6">
      <div className="card space-y-4">
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Google Calendar verbinden</h2>
        <p className="text-sm text-gray-500">
          Einmalige Einrichtung. Benötigt eine OAuth Client-ID aus der Google Cloud Console.
        </p>

        <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-3 text-xs text-blue-700 dark:text-blue-300 space-y-1">
          <p className="font-semibold">Einrichtung in Google Cloud Console:</p>
          <ol className="list-decimal ml-4 space-y-0.5">
            <li>APIs &amp; Services → OAuth 2.0-Client-IDs</li>
            <li>Anwendungstyp: Webanwendung</li>
            <li>Autorisierter Redirect-URI: <code className="bg-blue-100 dark:bg-blue-900/30 px-1 rounded">{window.location.origin}/auth/google/callback</code></li>
            <li>Google Calendar API aktivieren (APIs &amp; Services → Bibliothek)</li>
            <li>Scope: <code className="bg-blue-100 dark:bg-blue-900/30 px-1 rounded">calendar</code> (Lesen &amp; Schreiben)</li>
            <li><b>Client-ID <u>und</u> Client-Schlüssel</b> kopieren (beides steht bei der erstellten Client-ID)</li>
          </ol>
        </div>

        {oauthError !== null && (
          <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-xs text-red-700 dark:text-red-300">
            <p className="font-semibold">Letzter Verbindungsversuch fehlgeschlagen:</p>
            <p className="mt-1">{oauthError}</p>
          </div>
        )}

        <div className="space-y-1">
          <label className="label-text">Google OAuth Client-ID</label>
          <input type="text" value={clientId} onChange={e => { setClientId(e.target.value); }}
            placeholder="123456789-abc…apps.googleusercontent.com"
            className="input-field font-mono text-xs" />
        </div>

        <div className="space-y-1">
          <label className="label-text">Client-Schlüssel (Client Secret)</label>
          <input type="password" value={clientSecret} onChange={e => { setClientSecret(e.target.value); }}
            placeholder="GOCSPX-…"
            className="input-field font-mono text-xs" />
          <p className="text-xs text-gray-400">
            Bleibt nur lokal in diesem Gerät gespeichert. Nötig, weil Google für
            „Webanwendung"-Clients den Schlüssel verlangt.
          </p>
        </div>

        <div className="space-y-1">
          <label className="label-text">Kalender-ID</label>
          <input type="text" value={calendarId} onChange={e => { setCalendarId(e.target.value); }}
            placeholder="primary oder mail@gmail.com"
            className="input-field text-sm" />
          <p className="text-xs text-gray-400">„primary" = Standard-Kalender</p>
        </div>

        <button onClick={() => { void handleConnect(); }}
          disabled={!clientId.trim() || !clientSecret.trim() || saving}
          className="btn-primary w-full justify-center disabled:opacity-50">
          {saving ? 'Verbinde…' : '🔗 Mit Google Calendar verbinden'}
        </button>
      </div>

      <div className="card space-y-3 border-dashed">
        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Demo-Modus (ohne Google)</p>
        <p className="text-xs text-gray-500">
          Der End-of-Day-Review mit Smart-Matching funktioniert auch ohne Google Calendar-Verbindung.
        </p>
        <button onClick={onConnected} className="btn-secondary text-sm">
          Demo-Modus verwenden
        </button>
      </div>
    </div>
  );
}

// ── Termin-Formular (anlegen / bearbeiten) ────────────────────────────────

function isoToLocal(iso: string | undefined): string {
  if (iso === undefined || iso === '') return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function TerminForm({ calendars, defaultCalendarId, editEvent, onSave, onCancel }: {
  calendars: CalendarListEntry[];
  defaultCalendarId: string;
  editEvent: GoogleEvent | null;
  onSave: (calendarId: string, input: EventInput, eventId?: string) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [calId,   setCalId]   = useState(defaultCalendarId);
  const [summary, setSummary] = useState(editEvent?.summary ?? '');
  const [start,   setStart]   = useState(isoToLocal(editEvent?.start.dateTime) || isoToLocal(new Date().toISOString()));
  const [ende,    setEnde]    = useState(isoToLocal(editEvent?.end.dateTime) || isoToLocal(new Date(Date.now() + 3600_000).toISOString()));
  const [ort,     setOrt]     = useState(editEvent?.location ?? '');
  const [notiz,   setNotiz]   = useState(editEvent?.description ?? '');
  const [busy,    setBusy]    = useState(false);

  const valid = summary.trim() !== '' && start !== '' && ende !== '' && start < ende;

  const handleSave = (): void => {
    if (!valid) return;
    setBusy(true);
    onSave(calId, {
      summary: summary.trim(),
      startISO: start, endISO: ende,
      ...(notiz !== '' ? { description: notiz } : {}),
      ...(ort !== '' ? { location: ort } : {}),
    }, editEvent?.id);
  };

  return (
    <div className="card space-y-3">
      <h3 className="font-semibold text-gray-900 dark:text-gray-100">
        {editEvent !== null ? 'Termin bearbeiten' : 'Neuer Termin'}
      </h3>
      <div className="space-y-1">
        <label className="label-text">Titel *</label>
        <input value={summary} onChange={e => { setSummary(e.target.value); }}
          placeholder="z.B. Begehung Planai" className="input-field" />
      </div>
      <div className="space-y-1">
        <label className="label-text">Kalender</label>
        <select value={calId} onChange={e => { setCalId(e.target.value); }}
          className="input-field" disabled={editEvent !== null}>
          {calendars.map(c => (
            <option key={c.id} value={c.id}>{c.summary}{c.primary === true ? ' (Standard)' : ''}</option>
          ))}
        </select>
        {editEvent !== null && (
          <p className="text-xs text-gray-400">Kalender eines bestehenden Termins ist nicht änderbar.</p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="label-text">Von *</label>
          <input type="datetime-local" value={start} onChange={e => { setStart(e.target.value); }}
            className="input-field" />
        </div>
        <div className="space-y-1">
          <label className="label-text">Bis *</label>
          <input type="datetime-local" value={ende} onChange={e => { setEnde(e.target.value); }}
            className="input-field" />
        </div>
      </div>
      {start >= ende && start !== '' && (
        <p className="text-xs text-red-500">⚠ Ende muss nach Beginn liegen.</p>
      )}
      <div className="space-y-1">
        <label className="label-text">Ort</label>
        <input value={ort} onChange={e => { setOrt(e.target.value); }} className="input-field" />
      </div>
      <div className="space-y-1">
        <label className="label-text">Notiz</label>
        <textarea value={notiz} onChange={e => { setNotiz(e.target.value); }} rows={2} className="input-field" />
      </div>
      <div className="flex gap-2">
        <button onClick={handleSave} disabled={!valid || busy}
          className="btn-primary disabled:opacity-50">{busy ? 'Speichere…' : 'Speichern'}</button>
        <button onClick={onCancel} className="btn-secondary">Abbrechen</button>
      </div>
    </div>
  );
}

// ── Kalender-Ansicht ──────────────────────────────────────────────────────

function GCalView({ config, onDisconnect }: {
  config:       GCalConfig;
  onDisconnect: () => void;
}): React.JSX.Element {
  const [calendars, setCalendars] = useState<CalendarListEntry[]>([]);
  const [events,    setEvents]    = useState<GoogleEvent[]>([]);
  const [selCals,   setSelCals]   = useState<string[]>(
    config.calendarIds !== undefined && config.calendarIds.length > 0
      ? config.calendarIds
      : [config.calendarId]
  );
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [formOpen,  setFormOpen]  = useState(false);
  const [editEv,    setEditEv]    = useState<GoogleEvent | null>(null);
  const [protoIds,  setProtoIds]  = useState<Set<string>>(new Set());

  const requestHandoff = useHandoffStore(s => s.request);
  useEffect(() => { void eventIdsMitProtokoll().then(setProtoIds); }, [events]);

  function openProtokoll(ev: GoogleEvent, start: string): void {
    // Uhrzeit nur bei echten Zeit-Terminen (dateTime), nicht bei Ganztages-Terminen.
    const uhrzeit = ev.start.dateTime !== undefined && start.length >= 16 ? start.slice(11, 16) : '';
    const ende = ev.end.dateTime;
    const uhrzeitEnde = ende !== undefined && ende.length >= 16 ? ende.slice(11, 16) : '';
    requestHandoff('protokolle', {
      action: 'protokoll',
      event: {
        id: ev.id,
        titel: ev.summary ?? 'Termin',
        datum: start !== '' ? start.slice(0, 10) : new Date().toISOString().slice(0, 10),
        ...(uhrzeit !== '' ? { uhrzeit } : {}),
        ...(uhrzeitEnde !== '' ? { uhrzeit_ende: uhrzeitEnde } : {}),
        ...(ev.location !== undefined && ev.location !== '' ? { ort: ev.location } : {}),
        ...(ev.description !== undefined && ev.description !== '' ? { notizen: ev.description } : {}),
      },
    });
  }

  // Heutiger Zeitraum
  const today = new Date();
  const weekStart = new Date(today); weekStart.setDate(today.getDate() - today.getDay() + 1);
  const weekEnd   = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 7);

  const reloadEvents = (): void => {
    if (selCals.length === 0) { setEvents([]); return; }
    setLoading(true);
    listEventsMulti(selCals, weekStart.toISOString(), weekEnd.toISOString())
      .then(evts => { setEvents(evts); setError(''); })
      .catch(e => { setError(String(e)); })
      .finally(() => { setLoading(false); });
  };

  // Kalenderauswahl umschalten + dauerhaft speichern (Client-ID/Secret bleiben erhalten).
  const toggleCal = (id: string): void => {
    const next = selCals.includes(id) ? selCals.filter(c => c !== id) : [...selCals, id];
    setSelCals(next);
    void saveSetting<GCalConfig>(CONFIG_KEY, {
      ...config,
      calendarIds: next,
      ...(next[0] !== undefined ? { calendarId: next[0] } : {}),
    });
  };

  useEffect(() => {
    setLoading(true);
    listCalendars()
      .then(cals => { setCalendars(cals); })
      .catch(e => { setError(String(e)); })
      .finally(() => { setLoading(false); });
  }, []);

  useEffect(() => {
    reloadEvents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selCals]);

  const handleDisconnect = (): void => {
    clearTokens();
    onDisconnect();
  };

  const handleSaveTermin = (calendarId: string, input: EventInput, eventId?: string): void => {
    const op = eventId !== undefined
      ? updateEvent(calendarId, eventId, input)
      : createEvent(calendarId, input);
    void op
      .then(() => { setFormOpen(false); setEditEv(null); reloadEvents(); })
      .catch(e => { setError(e instanceof Error ? e.message : String(e)); });
  };

  const handleDeleteTermin = (ev: GoogleEvent): void => {
    if (!window.confirm(`Termin „${ev.summary}" löschen?`)) return;
    const cal = ev.calendarId ?? selCals[0] ?? config.calendarId;
    void deleteEvent(cal, ev.id)
      .then(reloadEvents)
      .catch(e => { setError(e instanceof Error ? e.message : String(e)); });
  };

  return (
    <div className="space-y-4">
      {/* Kalender-Auswahl (mehrere kombinierbar, Auswahl wird gespeichert) */}
      <div className="flex items-start gap-3 flex-wrap">
        {calendars.length > 0 && (
          <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
            {calendars.map(c => {
              const active = selCals.includes(c.id);
              return (
                <button key={c.id} onClick={() => { toggleCal(c.id); }}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors
                    ${active
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-blue-400'
                    }`}>
                  {active ? '✓ ' : ''}{c.summary}{c.primary === true ? ' (Standard)' : ''}
                </button>
              );
            })}
          </div>
        )}
        <button onClick={() => { setEditEv(null); setFormOpen(true); }}
          className="btn-primary text-sm ml-auto">
          + Termin
        </button>
        <button onClick={handleDisconnect}
          className="text-xs text-red-500 hover:text-red-700 font-medium">
          Google trennen
        </button>
      </div>

      {selCals.length === 0 && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Kein Kalender ausgewählt — oben mindestens einen antippen.
        </p>
      )}

      {formOpen && (
        <TerminForm
          calendars={calendars}
          defaultCalendarId={editEv?.calendarId ?? selCals[0] ?? config.calendarId}
          editEvent={editEv}
          onSave={handleSaveTermin}
          onCancel={() => { setFormOpen(false); setEditEv(null); }}
        />
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-950/20 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          ⚠ {error}
          {error.includes('Token') && (
            <button onClick={() => { void startGoogleOAuth(config.clientId); }}
              className="ml-2 underline">Neu anmelden</button>
          )}
        </div>
      )}

      {loading && <div className="text-center py-6 text-gray-400">Lade Kalender…</div>}

      {!loading && !error && (
        <div className="card overflow-hidden p-0">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
            <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">
              Diese Woche ({weekStart.toLocaleDateString('de-AT', { day: 'numeric', month: 'short' })} –
              {weekEnd.toLocaleDateString('de-AT', { day: 'numeric', month: 'short' })})
            </h3>
            <span className="text-xs text-gray-400">{events.length} Termine</span>
          </div>
          {events.length === 0 ? (
            <p className="text-center py-8 text-gray-400 italic text-sm">Keine Termine diese Woche</p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {events.map(ev => {
                const start = ev.start.dateTime ?? ev.start.date ?? '';
                const startDate = new Date(start);
                const match = matchEintrag({
                  id:           ev.id,
                  titel:        ev.summary ?? '',
                  startISO:     start,
                  endeISO:      ev.end.dateTime ?? ev.end.date ?? '',
                  ort:          ev.location,
                  beschreibung: ev.description,
                  status:       'unmatched',
                  mandantId:    '' as import('../../types/branded').MandantID,
                  quelle:       'google',
                  erstelltAm:   new Date().toISOString(),
                });
                return (
                  <div key={ev.id} className="group flex items-start gap-3 px-4 py-3">
                    <div className="w-12 text-center flex-shrink-0">
                      <p className="text-xs text-gray-400">{startDate.toLocaleDateString('de-AT', { weekday: 'short' })}</p>
                      <p className="text-sm font-bold text-gray-900 dark:text-gray-100">
                        {startDate.getHours() > 0
                          ? startDate.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' })
                          : startDate.toLocaleDateString('de-AT', { day: 'numeric', month: 'short' })
                        }
                      </p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{ev.summary}</p>
                      {selCals.length > 1 && ev.calendarId !== undefined && (
                        <p className="text-xs text-gray-400 truncate">
                          🗓 {calendars.find(c => c.id === ev.calendarId)?.summary ?? ev.calendarId}
                        </p>
                      )}
                      {ev.location !== undefined && ev.location !== '' && (
                        <p className="text-xs text-gray-400 truncate">📍 {ev.location}</p>
                      )}
                      {protoIds.has(ev.id) && (
                        <button onClick={() => { openProtokoll(ev, start); }}
                          className="text-xs text-blue-600 hover:underline mt-0.5">📝 Protokoll</button>
                      )}
                    </div>
                    {match.kategorie !== 'Unbekannt' && (
                      <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0
                        ${match.confidence >= 85 ? 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-400'
                          : match.confidence >= 65 ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-600'}`}>
                        {match.kategorie}
                      </span>
                    )}
                    <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => { openProtokoll(ev, start); }}
                        className="text-xs text-gray-400 hover:text-blue-600" title="Besprechungsprotokoll">📝</button>
                      <ErinnerungButton
                        titel={ev.summary ?? 'Termin'}
                        typ="TERMIN"
                        basisDatum={start !== '' ? start.slice(0, 10) : undefined}
                        quelle={`gcal:${ev.id}`}
                      />
                      <button onClick={() => { setEditEv(ev); setFormOpen(true); }}
                        className="text-xs text-gray-400 hover:text-blue-600" title="Bearbeiten">✎</button>
                      <button onClick={() => { handleDeleteTermin(ev); }}
                        className="text-xs text-gray-400 hover:text-red-500" title="Löschen">🗑</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Hauptkomponente ───────────────────────────────────────────────────────

type KalTab = 'review' | 'gcal';

export function KalenderModul(): React.JSX.Element {
  const [tab, setTab]     = useState<KalTab>('review');
  const [connected, setConnected] = useState(false);
  const [config,    setConfig]    = useState<GCalConfig | null>(null);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    // OAuth-Callback prüfen
    const params = new URLSearchParams(window.location.search);
    const code   = params.get('code');
    const state  = params.get('state');
    if (code !== null && state !== null) {
      // Callback-Handling erfolgt in eigenem Flow
      window.history.replaceState({}, '', window.location.pathname);
    }

    void loadSetting<GCalConfig | null>(CONFIG_KEY, null).then(cfg => {
      setConfig(cfg);
      setConnected(cfg !== null && isTokenValid());
      setLoading(false);
    });
  }, []);

  if (loading) {
    return <div className="flex-1 flex items-center justify-center text-gray-400">Lade…</div>;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 pt-4 flex gap-1">
        {([
          { key: 'review' as KalTab, label: 'End-of-Day Review', icon: '📅' },
          { key: 'gcal'   as KalTab, label: connected ? '🟢 Google Calendar' : 'Google Calendar', icon: connected ? '' : '📆' },
        ]).map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); }}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors
              ${tab === t.key
                ? 'border-blue-600 text-blue-700 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}>
            {t.icon && <span>{t.icon}</span>}
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-6 max-w-4xl mx-auto w-full">
        {tab === 'review' && <EndOfDayReview />}
        {tab === 'gcal' && (
          connected && config !== null
            ? <GCalView config={config} onDisconnect={() => { setConnected(false); setConfig(null); }} />
            : <GCalSetup onConnected={() => { setConnected(isTokenValid()); }} />
        )}
      </div>
    </div>
  );
}
